/**
 * Maintenance: what is being worked on, and what is due.
 *
 * A tool room's core job is not knowing where moulds are - it is keeping them
 * running. This is the screen that answers "what was done to this tool, what is
 * due, and what did it cost", none of which the system could answer before.
 *
 * Two tabs on purpose. Jobs are work that exists; schedules are work that will.
 * Mixing them puts a plan and a problem in the same list, and the only thing
 * anyone wants from that list at 8am is the problems.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarClock,
  CheckCircle2,
  Plus,
  Search,
  Wrench,
} from 'lucide-react';
import {
  ApiClientError,
  type CreateJobInput,
  type MaintenanceJobRow,
  type MaintenanceScheduleRow,
  type MaintenanceStatus,
  type MaintenanceType,
} from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { DataTable, type Column } from '../../components/data/DataTable.js';
import { Button } from '../../components/ui/button.js';
import { Dialog } from '../../components/ui/dialog.js';
import { Field, Input, Select, Textarea } from '../../components/ui/field.js';
import { Menu, MenuItem, MenuLabel } from '../../components/ui/menu.js';
import { Badge, Code, EmptyState } from '../../components/ui/primitives.js';
import {
  createJob,
  createSchedule,
  deleteSchedule,
  isNetworkError,
  listJobs,
  listSchedules,
  updateJob,
} from '../../api/client.js';
import { useCan } from '../../auth/AuthProvider.js';
import { useMoulds } from '../../db/queries.js';
import { cn } from '../../lib/cn.js';

const TYPE_LABEL: Record<MaintenanceType, string> = {
  PREVENTIVE: 'Preventive',
  BREAKDOWN: 'Breakdown',
  MODIFICATION: 'Modification',
  REFURBISHMENT: 'Refurbishment',
  INSPECTION: 'Inspection',
};

const STATUS_LABEL: Record<MaintenanceStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  ON_HOLD: 'On hold',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

const STATUS_TONE: Record<MaintenanceStatus, 'neutral' | 'primary' | 'warning' | 'success' | 'danger'> = {
  OPEN: 'primary',
  IN_PROGRESS: 'warning',
  ON_HOLD: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString();
}

export function MaintenancePage() {
  const canWrite = useCan('create', 'moulds');
  const [tab, setTab] = useState<'jobs' | 'schedules'>('jobs');
  const [jobs, setJobs] = useState<MaintenanceJobRow[] | undefined>(undefined);
  const [schedules, setSchedules] = useState<MaintenanceScheduleRow[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  const [newJob, setNewJob] = useState(false);
  const [newSchedule, setNewSchedule] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [j, s] = await Promise.all([listJobs(), listSchedules()]);
      setJobs(j);
      setSchedules(s);
    } catch (e) {
      setJobs([]);
      setSchedules([]);
      setError(
        isNetworkError(e)
          ? 'No connection. Maintenance is managed online.'
          : 'Could not load maintenance.',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openJobs = useMemo(
    () => (jobs ?? []).filter((j) => j.status !== 'COMPLETED' && j.status !== 'CANCELLED'),
    [jobs],
  );

  const summary = useMemo(() => {
    const now = Date.now();
    return {
      open: openJobs.length,
      overdue: openJobs.filter((j) => j.dueAt && new Date(j.dueAt).getTime() < now).length,
      breakdowns: openJobs.filter((j) => j.type === 'BREAKDOWN').length,
      dueSchedules: (schedules ?? []).filter((s) => s.due).length,
    };
  }, [openJobs, schedules]);

  const visibleJobs = useMemo(() => {
    const base = showClosed ? (jobs ?? []) : openJobs;
    const needle = search.trim().toLowerCase();
    if (!needle) return base;
    return base.filter((j) =>
      `${j.jobNo} ${j.summary} ${j.mould?.assetCode ?? ''} ${j.mould?.name ?? ''}`
        .toLowerCase()
        .includes(needle),
    );
  }, [jobs, openJobs, showClosed, search]);

  async function setStatus(job: MaintenanceJobRow, status: MaintenanceStatus) {
    setError(null);
    try {
      await updateJob(job.id, { status });
      await load();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'That change could not be applied.');
    }
  }

  const jobColumns: Array<Column<MaintenanceJobRow>> = [
    {
      key: 'jobNo',
      header: 'Job',
      primary: true,
      sortValue: (j) => j.jobNo,
      render: (j) => <Code className="font-semibold">{j.jobNo}</Code>,
    },
    {
      key: 'mould',
      header: 'Mould',
      sortValue: (j) => j.mould?.assetCode ?? '',
      render: (j) => (
        <span className="flex min-w-0 items-center gap-2">
          <Code>{j.mould?.assetCode ?? '-'}</Code>
          <span className="truncate text-muted-foreground">{j.mould?.name}</span>
        </span>
      ),
    },
    {
      key: 'summary',
      header: 'Work',
      sortValue: (j) => j.summary,
      render: (j) => <span className="truncate">{j.summary}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      hideOnMobile: true,
      sortValue: (j) => j.type,
      render: (j) => (
        <Badge tone={j.type === 'BREAKDOWN' ? 'danger' : 'neutral'}>{TYPE_LABEL[j.type]}</Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (j) => j.status,
      render: (j) => <Badge tone={STATUS_TONE[j.status]}>{STATUS_LABEL[j.status]}</Badge>,
    },
    {
      key: 'dueAt',
      header: 'Due',
      hideOnMobile: true,
      sortValue: (j) => j.dueAt ?? '',
      render: (j) => {
        const late = j.dueAt && new Date(j.dueAt).getTime() < Date.now();
        const open = j.status !== 'COMPLETED' && j.status !== 'CANCELLED';
        return (
          <span className={late && open ? 'text-danger' : 'text-muted-foreground'}>
            {fmtDate(j.dueAt)}
            {late && open ? ' · overdue' : ''}
          </span>
        );
      },
    },
    {
      key: 'assignedTo',
      header: 'Assigned',
      hideOnMobile: true,
      sortValue: (j) => j.assignedTo?.name ?? '',
      render: (j) => j.assignedTo?.name ?? <span className="text-subtle-foreground">-</span>,
    },
  ];

  const scheduleColumns: Array<Column<MaintenanceScheduleRow>> = [
    {
      key: 'mould',
      header: 'Mould',
      primary: true,
      sortValue: (s) => s.mould?.assetCode ?? '',
      render: (s) => (
        <span className="flex min-w-0 items-center gap-2">
          <Code className="font-semibold">{s.mould?.assetCode ?? '-'}</Code>
          <span className="truncate text-muted-foreground">{s.mould?.name}</span>
        </span>
      ),
    },
    {
      key: 'summary',
      header: 'Service',
      sortValue: (s) => s.summary,
      render: (s) => s.summary,
    },
    {
      key: 'every',
      header: 'Every',
      sortValue: (s) => s.everyDays ?? s.everyShots ?? 0,
      render: (s) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {[
            s.everyDays ? `${s.everyDays} days` : null,
            s.everyShots ? `${s.everyShots.toLocaleString()} shots` : null,
          ]
            .filter(Boolean)
            .join(' or ')}
        </span>
      ),
    },
    {
      key: 'next',
      header: 'Next due',
      sortValue: (s) => s.nextDueAt ?? '',
      render: (s) => (
        <span className={s.due ? 'font-medium text-danger' : 'text-muted-foreground'}>
          {fmtDate(s.nextDueAt)}
          {s.nextDueShots != null ? ` · ${s.nextDueShots.toLocaleString()} shots` : ''}
          {s.due ? ' · due' : ''}
        </span>
      ),
    },
    {
      key: 'lastDone',
      header: 'Last done',
      hideOnMobile: true,
      sortValue: (s) => s.lastDoneAt ?? '',
      render: (s) => <span className="text-muted-foreground">{fmtDate(s.lastDoneAt)}</span>,
    },
  ];

  return (
    <Page
      title="Maintenance"
      description="Work in progress, and what falls due next."
      actions={
        canWrite ? (
          <>
            <Button variant="secondary" onClick={() => setNewSchedule(true)}>
              <CalendarClock className="size-4" /> New schedule
            </Button>
            <Button variant="primary" onClick={() => setNewJob(true)}>
              <Plus className="size-4" /> Raise job
            </Button>
          </>
        ) : null
      }
      toolbar={
        <div className="flex flex-col gap-2">
          <div className="flex gap-1 rounded-lg border border-border bg-surface p-1 sm:w-fit">
            {(['jobs', 'schedules'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium sm:flex-none',
                  tab === t ? 'bg-primary text-primary-foreground' : 'hover:bg-surface-sunken',
                )}
              >
                {t === 'jobs' ? `Jobs (${openJobs.length})` : `Schedules (${schedules?.length ?? 0})`}
              </button>
            ))}
          </div>

          {tab === 'jobs' ? (
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by job number, mould or work…"
                  className="pl-9"
                  type="search"
                  aria-label="Search jobs"
                />
              </div>
              <Button
                variant={showClosed ? 'outline' : 'secondary'}
                onClick={() => setShowClosed((v) => !v)}
              >
                <CheckCircle2 className="size-4" />
                {showClosed ? 'Hiding nothing' : 'Show closed'}
              </Button>
            </div>
          ) : null}
        </div>
      }
    >
      {error ? (
        <p role="alert" className="mb-3 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {jobs?.length || schedules?.length ? (
        <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Tile label="Open jobs" value={summary.open} />
          <Tile label="Overdue" value={summary.overdue} tone={summary.overdue ? 'danger' : undefined} />
          <Tile
            label="Breakdowns open"
            value={summary.breakdowns}
            tone={summary.breakdowns ? 'danger' : undefined}
          />
          <Tile
            label="Services due"
            value={summary.dueSchedules}
            tone={summary.dueSchedules ? 'warning' : undefined}
          />
        </div>
      ) : null}

      {tab === 'jobs' ? (
        <DataTable
          rows={visibleJobs}
          columns={jobColumns}
          rowKey={(j) => j.id}
          initialSort={{ key: 'dueAt', direction: 'asc' }}
          rowActions={
            canWrite
              ? (j) =>
                  j.status === 'COMPLETED' || j.status === 'CANCELLED' ? null : (
                    <Menu
                      trigger={
                        <Button variant="ghost" size="sm">
                          Update
                        </Button>
                      }
                    >
                      <MenuLabel>Move to</MenuLabel>
                      {(['OPEN', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED', 'CANCELLED'] as const)
                        .filter((s) => s !== j.status)
                        .map((s) => (
                          <MenuItem key={s} onSelect={() => void setStatus(j, s)}>
                            {STATUS_LABEL[s]}
                          </MenuItem>
                        ))}
                    </Menu>
                  )
              : undefined
          }
          empty={
            <EmptyState
              icon={<Wrench />}
              title={showClosed ? 'No jobs yet' : 'Nothing open'}
              description={
                showClosed
                  ? 'Raise a job when a mould needs work - a service, a breakdown, a modification.'
                  : 'No open maintenance. Turn on "Show closed" to see finished work.'
              }
              action={
                canWrite ? (
                  <Button variant="primary" onClick={() => setNewJob(true)}>
                    <Plus className="size-4" /> Raise job
                  </Button>
                ) : null
              }
            />
          }
        />
      ) : (
        <DataTable
          rows={schedules}
          columns={scheduleColumns}
          rowKey={(s) => s.id}
          initialSort={{ key: 'next', direction: 'asc' }}
          rowActions={
            canWrite
              ? (s) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void deleteSchedule(s.id).then(load);
                    }}
                  >
                    Remove
                  </Button>
                )
              : undefined
          }
          empty={
            <EmptyState
              icon={<CalendarClock />}
              title="No schedules"
              description="A schedule turns a service interval into something the system watches - every 90 days, every 100,000 shots, or both."
              action={
                canWrite ? (
                  <Button variant="primary" onClick={() => setNewSchedule(true)}>
                    <CalendarClock className="size-4" /> New schedule
                  </Button>
                ) : null
              }
            />
          }
        />
      )}

      {newJob ? (
        <JobDialog open onOpenChange={setNewJob} onSaved={() => void load()} />
      ) : null}
      {newSchedule ? (
        <ScheduleDialog open onOpenChange={setNewSchedule} onSaved={() => void load()} />
      ) : null}
    </Page>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'warning' | 'danger';
}) {
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-3">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div
        className={
          tone === 'danger'
            ? 'text-2xl font-semibold text-danger'
            : tone === 'warning'
              ? 'text-2xl font-semibold text-warning-foreground'
              : 'text-2xl font-semibold'
        }
      >
        {value}
      </div>
    </div>
  );
}

/** Raise a job against a mould. */
function JobDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const moulds = useMoulds({});
  const [mouldId, setMouldId] = useState('');
  const [type, setType] = useState<MaintenanceType>('PREVENTIVE');
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const mouldOptions = useMemo(
    () =>
      (moulds ?? [])
        .filter((m) => m.assetCode)
        .map((m) => ({
          value: m.id,
          label: m.name && m.name !== m.assetCode ? `${m.assetCode} - ${m.name}` : m.assetCode,
        })),
    [moulds],
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!mouldId || !summary.trim()) {
      setError('Choose a mould and say what the job is.');
      return;
    }
    setSaving(true);
    try {
      const input: CreateJobInput = {
        mouldId,
        type,
        summary: summary.trim(),
        details: details.trim() || null,
        dueAt: dueAt ? new Date(`${dueAt}T23:59:59`).toISOString() : null,
      };
      await createJob(input);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : isNetworkError(err)
            ? 'No connection. Jobs are raised online.'
            : 'Could not raise the job.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title="Raise a maintenance job"
      description="Records work that needs doing, or work that has already started."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="job-form" loading={saving}>
            Raise job
          </Button>
        </>
      }
    >
      <form id="job-form" onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Mould" required>
          <Select
            value={mouldId}
            onChange={(e) => setMouldId(e.target.value)}
            placeholder="Choose a mould"
            options={mouldOptions}
          />
        </Field>

        <Field
          label="Type"
          hint={
            type === 'BREAKDOWN'
              ? 'A breakdown takes the tool out of service when work starts.'
              : type === 'INSPECTION'
                ? 'An inspection leaves the tool where it is.'
                : undefined
          }
        >
          <Select
            value={type}
            onChange={(e) => setType(e.target.value as MaintenanceType)}
            options={Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label }))}
          />
        </Field>

        <Field label="What is the job" required>
          <Input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Replace ejector pins on cavity 4"
          />
        </Field>

        <Field label="Details">
          <Textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3} />
        </Field>

        <Field label="Due by" hint="Drives the overdue count on this screen.">
          <Input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
        </Field>

        {error ? (
          <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

/** A recurring service, by days, by shots, or by whichever comes first. */
function ScheduleDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const moulds = useMoulds({});
  const [mouldId, setMouldId] = useState('');
  const [summary, setSummary] = useState('');
  const [everyDays, setEveryDays] = useState('');
  const [everyShots, setEveryShots] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const mouldOptions = useMemo(
    () =>
      (moulds ?? [])
        .filter((m) => m.assetCode)
        .map((m) => ({
          value: m.id,
          label: m.name && m.name !== m.assetCode ? `${m.assetCode} - ${m.name}` : m.assetCode,
        })),
    [moulds],
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!mouldId || !summary.trim()) {
      setError('Choose a mould and say what the service is.');
      return;
    }
    if (!everyDays && !everyShots) {
      setError('Set a day interval, a shot interval, or both - otherwise it can never come due.');
      return;
    }
    setSaving(true);
    try {
      await createSchedule({
        mouldId,
        type: 'PREVENTIVE',
        summary: summary.trim(),
        everyDays: everyDays ? Number(everyDays) : null,
        everyShots: everyShots ? Number(everyShots) : null,
      });
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : isNetworkError(err)
            ? 'No connection. Schedules are created online.'
            : 'Could not create the schedule.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title="New maintenance schedule"
      description="Turns a service interval into something the system watches."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="schedule-form" loading={saving}>
            Create schedule
          </Button>
        </>
      }
    >
      <form id="schedule-form" onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Mould" required>
          <Select
            value={mouldId}
            onChange={(e) => setMouldId(e.target.value)}
            placeholder="Choose a mould"
            options={mouldOptions}
          />
        </Field>

        <Field label="What is the service" required>
          <Input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Full clean and grease"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Every N days">
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              value={everyDays}
              onChange={(e) => setEveryDays(e.target.value)}
              placeholder="90"
            />
          </Field>
          <Field label="Every N shots">
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              value={everyShots}
              onChange={(e) => setEveryShots(e.target.value)}
              placeholder="100000"
            />
          </Field>
        </div>

        {/* Worth saying, because "or" versus "and" is the whole behaviour. */}
        <p className="text-sm text-muted-foreground">
          Set both and the service falls due on whichever comes first. Time alone is wrong for a
          tool that sat idle; shots alone is wrong for one that degrades on the shelf.
        </p>

        {error ? (
          <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
